import { useState } from 'react';
import { useWithdrawApplication } from '../../hooks/unified/useApplicationsQueries';
import { useToast } from '../../contexts/ToastContext';
import type { ApplicationWithDetails } from '../../services/unified/types';

interface ApplicationTimelineProps {
  applications: ApplicationWithDetails[];
  onUpdate?: () => void;
}

export function ApplicationTimeline({ applications, onUpdate }: ApplicationTimelineProps) {
  const [selectedApplication, setSelectedApplication] = useState<ApplicationWithDetails | null>(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState<string | null>(null);
  const withdrawMutation = useWithdrawApplication();
  const { showSuccess, showError } = useToast();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'submitted':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
            <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case 'reviewing':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100">
            <svg className="h-4 w-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
        );
      case 'interview':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
            <svg className="h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        );
      case 'offer':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
            <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'withdrawn':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'text-blue-600';
      case 'reviewing': return 'text-yellow-600';
      case 'interview': return 'text-purple-600';
      case 'offer': return 'text-green-600';
      case 'rejected': return 'text-red-600';
      case 'withdrawn': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800';
      case 'interview': return 'bg-purple-100 text-purple-800';
      case 'offer': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'withdrawn': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getNextSteps = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'Your application has been submitted and is being processed. You should hear back within 1-2 weeks.';
      case 'reviewing':
        return 'Your application is currently under review by the hiring team. This process typically takes 3-5 business days.';
      case 'interview':
        return 'Congratulations! You\'ve been selected for an interview. Prepare by researching the company and practicing common interview questions.';
      case 'offer':
        return 'Congratulations! You\'ve received a job offer. Review the terms carefully and respond within the specified timeframe.';
      case 'rejected':
        return 'Unfortunately, you were not selected for this position. Keep applying to other opportunities and consider asking for feedback.';
      case 'withdrawn':
        return 'You have withdrawn your application for this position.';
      default:
        return 'Status update pending.';
    }
  };

  // Sort applications by most recent first
  const sortedApplications = [...applications].sort((a, b) => 
    new Date(b.updated_at || '').getTime() - new Date(a.updated_at || '').getTime()
  );

  return (
    <div className="p-3 sm:p-6">
      <div className="flow-root">
        <ul className="-mb-8">
          {sortedApplications.map((application, applicationIdx) => (
            <li key={application.id}>
              <div className="relative pb-6 sm:pb-8">
                {applicationIdx !== sortedApplications.length - 1 ? (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="relative flex space-x-3">
                  <div className="flex-shrink-0">{getStatusIcon(application.status)}</div>
                  <div className="flex-1 min-w-0">
                    {/* Mobile: Stacked layout */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:space-x-4">
                      <div className="min-w-0 flex-1">
                        {/* Job title and status */}
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          {application.company_logo_url && (
                            <img
                              src={application.company_logo_url}
                              alt={application.job_company || 'Company'}
                              className="h-5 w-5 sm:h-6 sm:w-6 rounded object-cover flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {application.job_title || 'Job Title'}
                          </h3>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(application.status)}`}>
                            {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                          </span>
                        </div>
                        
                        {/* Company and location */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
                          <p className="text-sm text-gray-600">{application.job_company || 'Company'}</p>
                          {application.job_location && (
                            <>
                              <span className="text-gray-400 hidden sm:inline">•</span>
                              <p className="text-xs text-gray-500">{application.job_location}</p>
                            </>
                          )}
                        </div>
                        
                        {/* Salary */}
                        {application.job_salary_min && application.job_salary_max && (
                          <p className="text-xs text-gray-600 mb-1">
                            ₹{application.job_salary_min.toLocaleString()} - ₹{application.job_salary_max.toLocaleString()}
                          </p>
                        )}
                        
                        {/* Date info - simplified on mobile */}
                        <div className="text-xs text-gray-500 mb-2 space-y-0.5 sm:space-y-0">
                          <p>Applied {formatDate(application.applied_at || '')}</p>
                          {application.resume_filename && (
                            <p className="flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="truncate">{application.resume_label || application.resume_filename}</span>
                            </p>
                          )}
                        </div>

                        {/* Action buttons - touch friendly */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          <button
                            onClick={() => setSelectedApplication(
                              selectedApplication?.id === application.id ? null : application
                            )}
                            className="px-3 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium min-h-[44px] flex items-center"
                          >
                            {selectedApplication?.id === application.id ? 'Hide Details' : 'View Details'}
                          </button>
                          {application.status !== 'withdrawn' && application.status !== 'rejected' && (
                            <button
                              onClick={() => setShowWithdrawConfirm(application.id)}
                              disabled={withdrawMutation.isPending}
                              className="px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium disabled:opacity-50 min-h-[44px] flex items-center"
                            >
                              Withdraw
                            </button>
                          )}
                        </div>

                        {/* Expanded details */}
                        {selectedApplication?.id === application.id && (
                          <div className="mt-4 p-3 sm:p-4 bg-blue-50 rounded-lg">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">Next Steps</h4>
                            <p className="text-sm text-blue-800 mb-3">
                              {getNextSteps(application.status)}
                            </p>
                            
                            {/* Details grid - single column on mobile */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                              <div className="flex flex-col sm:flex-row sm:items-center">
                                <span className="font-medium text-blue-900">Application ID:</span>
                                <span className="sm:ml-2 text-blue-800 font-mono text-xs break-all">{application.id}</span>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center">
                                <span className="font-medium text-blue-900">Status:</span>
                                <span className={`sm:ml-2 font-medium ${getStatusColor(application.status)}`}>
                                  {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                                </span>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center">
                                <span className="font-medium text-blue-900">Submitted:</span>
                                <span className="sm:ml-2 text-blue-800 text-xs">{formatDateTime(application.applied_at || '')}</span>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center">
                                <span className="font-medium text-blue-900">Last Updated:</span>
                                <span className="sm:ml-2 text-blue-800 text-xs">{formatDateTime(application.updated_at || '')}</span>
                              </div>
                            </div>

                            {application.status === 'interview' && (
                              <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                                <h5 className="text-sm font-medium text-blue-900 mb-2">Interview Prep Tips</h5>
                                <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                                  <li>• Research the company's mission and values</li>
                                  <li>• Prepare examples of your relevant skills</li>
                                  <li>• Practice common interview questions</li>
                                  <li>• Prepare questions about the role</li>
                                  <li>• Test your tech for virtual interviews</li>
                                </ul>
                              </div>
                            )}

                            {application.status === 'offer' && (
                              <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
                                <h5 className="text-sm font-medium text-green-900 mb-2">Offer Checklist</h5>
                                <ul className="text-xs sm:text-sm text-green-800 space-y-1">
                                  <li>• Review salary and benefits</li>
                                  <li>• Consider work-life balance</li>
                                  <li>• Evaluate growth opportunities</li>
                                  <li>• Negotiate if appropriate</li>
                                  <li>• Respond within the timeframe</li>
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Date on right - hidden on mobile, shown in card instead */}
                      <div className="hidden sm:block whitespace-nowrap text-right text-sm text-gray-500 pt-1.5">
                        <time dateTime={application.updated_at}>
                          {formatDate(application.updated_at || '')}
                        </time>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Withdraw Confirmation Modal - slides up on mobile */}
      {showWithdrawConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-5 sm:p-6 w-full sm:max-w-md shadow-xl">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 hidden sm:block">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Withdraw Application</h3>
                <p className="text-sm text-gray-600 mb-1">
                  Are you sure you want to withdraw your application for{' '}
                  <span className="font-medium">
                    {applications.find(a => a.id === showWithdrawConfirm)?.job_title}
                  </span>?
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  This action cannot be undone.
                </p>
                <div className="flex flex-col-reverse sm:flex-row sm:space-x-3 gap-2 sm:gap-0 sm:justify-end">
                  <button
                    onClick={() => setShowWithdrawConfirm(null)}
                    disabled={withdrawMutation.isPending}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await withdrawMutation.mutateAsync(showWithdrawConfirm);
                        showSuccess('Application withdrawn successfully');
                        setShowWithdrawConfirm(null);
                        onUpdate?.();
                      } catch {
                        showError('Failed to withdraw application');
                      }
                    }}
                    disabled={withdrawMutation.isPending}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 min-h-[44px]"
                  >
                    {withdrawMutation.isPending ? 'Withdrawing...' : 'Withdraw'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}